import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MwSelector, MwSpinnerButton, NoDataFound } from '../ui'
import TicketsListItem from './TicketsListItem'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useCurrent } from '../../hooks'
import { useTranslation } from 'react-i18next'
import { getLocalizedStatusOptions, getPriorityOptions, getStatusAppearance, getStatusId, matchesStatusGroup, normalizeStatusValue } from '../../constants/ticket'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'

const TicketsList = ({ data, loading, supportSettings }) => {
    const { currentLangId } = useCurrent();
    const [t, i18n] = useTranslation('global');
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sectionFilterValue, setSectionFilterValue] = useState('all');
    const [productFilterValue, setProductFilterValue] = useState('all');
    const [priorityFilterValue, setPriorityFilterValue] = useState('all');
    const [statusFilterValue, setStatusFilterValue] = useState('all');
    const [statusGroupFilterValue, setStatusGroupFilterValue] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;
    const rows = useMemo(() => data?.data || [], [data]);
    const isRtl = i18n.dir() === 'rtl';

    const filtersStorageKey = useMemo(
        () => `support:tickets-filters:user:${String(params?.tenant || 'default')}`,
        [params?.tenant]
    );
    const safeParseJson = (value) => {
        try {
            return JSON.parse(String(value || ''));
        } catch {
            return null;
        }
    };
    const readStoredFilters = () => safeParseJson(localStorage.getItem(filtersStorageKey));
    const writeStoredFilters = (payload) => {
        try {
            localStorage.setItem(filtersStorageKey, JSON.stringify(payload || {}));
        } catch {
        }
    };

    const didHydrateFromStorageRef = useRef(false);
    useEffect(() => {
        didHydrateFromStorageRef.current = false;
    }, [filtersStorageKey]);

    useEffect(() => {
        if (didHydrateFromStorageRef.current) return;
        const current = new URLSearchParams(searchParams.toString());
        const keys = ['section', 'product', 'priority', 'status', 'statusGroup', 'page'];
        const hasAnyQuery = keys.some((key) => current.get(key) != null);
        if (hasAnyQuery) {
            didHydrateFromStorageRef.current = true;
            return;
        }
        const stored = readStoredFilters();
        didHydrateFromStorageRef.current = true;
        if (!stored || typeof stored !== 'object') return;
        const next = new URLSearchParams(current.toString());
        const setIf = (key, value) => {
            const resolved = value == null ? '' : String(value).trim();
            if (resolved) next.set(key, resolved);
        };
        setIf('section', stored.section && stored.section !== 'all' ? stored.section : '');
        setIf('product', stored.product && stored.product !== 'all' ? stored.product : '');
        setIf('priority', stored.priority && stored.priority !== 'all' ? stored.priority : '');
        setIf('status', stored.status && stored.status !== 'all' ? stored.status : '');
        setIf('statusGroup', stored.statusGroup && stored.statusGroup !== 'all' ? stored.statusGroup : '');
        const storedPage = Number(stored.page) || 1;
        if (storedPage > 1) next.set('page', String(storedPage));
        if (next.toString() !== current.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [filtersStorageKey, searchParams, setSearchParams]);

    const sectionOptions = useMemo(() => [
        { label: t('support.tickets.allSections'), value: 'all', lang: currentLangId },
        ...(supportSettings?.sections || [])
            .filter((item) => item?.isActive !== false)
            .map((item) => ({
                label: currentLangId === 'ar' ? item.labelAr : item.labelEn,
                value: item.key,
                lang: currentLangId,
            })),
    ], [currentLangId, supportSettings?.sections, t]);

    const productOptions = useMemo(() => [
        { label: t('support.tickets.allProducts'), value: 'all', lang: currentLangId },
        ...(supportSettings?.products || [])
            .filter((item) => item?.isActive !== false)
            .map((item) => ({
                label: currentLangId === 'ar' ? item.labelAr : item.labelEn,
                value: item.key,
                lang: currentLangId,
            })),
    ], [currentLangId, supportSettings?.products, t]);

    const priorityOptions = useMemo(() => [
        { label: currentLangId === 'ar' ? 'كل الأولويات' : 'All priorities', value: 'all', lang: currentLangId },
        ...getPriorityOptions(currentLangId),
    ], [currentLangId]);

    const selectedSectionFilter = useMemo(
        () => sectionOptions.find((item) => item.value === sectionFilterValue) || sectionOptions[0],
        [sectionFilterValue, sectionOptions]
    );
    const selectedProductFilter = useMemo(
        () => productOptions.find((item) => item.value === productFilterValue) || productOptions[0],
        [productFilterValue, productOptions]
    );
    const selectedPriorityFilter = useMemo(
        () => priorityOptions.find((item) => item.value === priorityFilterValue) || priorityOptions[0],
        [priorityFilterValue, priorityOptions]
    );
    const statusCountRows = useMemo(() => rows.filter((item) => {
        const matchesSection = sectionFilterValue === 'all' || item.sectionId === sectionFilterValue;
        const matchesProduct = productFilterValue === 'all' || item.ProductId === productFilterValue;
        const matchesPriority = priorityFilterValue === 'all' || String(item?.priority || 'medium') === priorityFilterValue;
        return matchesSection && matchesProduct && matchesPriority;
    }), [priorityFilterValue, productFilterValue, rows, sectionFilterValue]);

    const statusCounts = useMemo(() => {
        const counts = new Map();
        (statusCountRows || []).forEach((item) => {
            const currentStatusValue = item?.statusId || item?.status;
            const statusId = getStatusId(supportSettings?.statuses, currentStatusValue);
            if (!statusId) return;
            counts.set(statusId, (counts.get(statusId) || 0) + 1);
        });
        return counts;
    }, [statusCountRows, supportSettings?.statuses]);

    const filteredRows = useMemo(() => rows.filter((item) => {
        const matchesSection = sectionFilterValue === 'all' || item.sectionId === sectionFilterValue;
        const matchesProduct = productFilterValue === 'all' || item.ProductId === productFilterValue;
        const matchesPriority = priorityFilterValue === 'all' || String(item?.priority || 'medium') === priorityFilterValue;
        const currentStatusValue = item.statusId || item.status;
        const matchesStatus = statusFilterValue === 'all' || getStatusId(supportSettings?.statuses, currentStatusValue) === statusFilterValue;
        const matchesStatusCategory = matchesStatusGroup(supportSettings?.statuses, currentStatusValue, statusGroupFilterValue);
        return matchesSection && matchesProduct && matchesPriority && matchesStatus && matchesStatusCategory;
    }), [priorityFilterValue, productFilterValue, rows, sectionFilterValue, statusFilterValue, statusGroupFilterValue, supportSettings?.statuses]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
    const numberedPagesTotal = totalPages;
    const hasPaginationControls = filteredRows.length > itemsPerPage;
    const hasActiveListFilters = useMemo(() => {
        const activeQueryKeys = ['section', 'product', 'priority', 'status', 'statusGroup'];
        const hasActiveQuery = activeQueryKeys.some((key) => {
            const value = String(searchParams.get(key) || '').trim();
            return value && value !== 'all';
        });
        return hasActiveQuery ||
            sectionFilterValue !== 'all' ||
            productFilterValue !== 'all' ||
            priorityFilterValue !== 'all' ||
            statusFilterValue !== 'all' ||
            statusGroupFilterValue !== 'all';
    }, [priorityFilterValue, productFilterValue, searchParams, sectionFilterValue, statusFilterValue, statusGroupFilterValue]);
    const paginatedRows = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredRows.slice(startIndex, startIndex + itemsPerPage);
    }, [currentPage, filteredRows, itemsPerPage]);

    useEffect(() => {
        const query = searchParams.toString();
        const nextSearchParams = new URLSearchParams(query);
        const requestedSection = nextSearchParams.get('section') || 'all';
        const requestedProduct = nextSearchParams.get('product') || 'all';
        const requestedPriority = nextSearchParams.get('priority') || 'all';
        const requestedPage = Math.max(1, parseInt(nextSearchParams.get('page') || '1', 10) || 1);
        const requestedStatus = normalizeStatusValue(nextSearchParams.get('status') || 'all');
        const requestedStatusGroup = (nextSearchParams.get('statusGroup') || 'all').toLowerCase();
        setSectionFilterValue(requestedSection || 'all');
        setProductFilterValue(requestedProduct || 'all');
        setPriorityFilterValue(requestedPriority || 'all');
        setStatusFilterValue(requestedStatus || 'all');
        setStatusGroupFilterValue(requestedStatusGroup || 'all');
        setCurrentPage(requestedPage);

        writeStoredFilters({
            section: requestedSection || 'all',
            product: requestedProduct || 'all',
            priority: requestedPriority || 'all',
            status: requestedStatus || 'all',
            statusGroup: requestedStatusGroup || 'all',
            page: requestedPage
        });
    }, [searchParams.toString()]);

    useEffect(() => {
        if (currentPage > totalPages) {
            const next = Math.max(1, totalPages);
            const nextParams = new URLSearchParams(searchParams.toString());
            if (next <= 1) nextParams.delete('page');
            else nextParams.set('page', String(next));
            setSearchParams(nextParams, { replace: true });
        }
    }, [currentPage, totalPages]);

    const cols = [
        { key: t('support.tickets.ticketNumber'), width: 120, align: 'center' },
        { key: t('support.tickets.subject'), flex: 1, align: isRtl ? 'right' : 'left' },
        { key: `${t('support.tickets.section')} / ${t('support.tickets.product')}`, width: 230, align: 'center' },
        { key: t('support.tickets.priority'), width: 150, align: 'center' },
        { key: `${t('support.tickets.createdAt')} / ${t('support.tickets.updatedAt')}`, width: 170, align: 'center' },
    ];

    const gridTemplate = cols.map(c => c.flex ? '1fr' : `${c.width}px`).join(' ');

    return (
        <div style={{ fontFamily: "'Cairo', 'Segoe UI', sans-serif", direction: isRtl ? 'rtl' : 'ltr' }}>
            {loading && rows.length === 0 && !hasActiveListFilters ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
                    <MwSpinnerButton withLabel={false} isFullCenter />
                </div>
            ) : rows.length > 0 || hasActiveListFilters ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 16,
                        border: '1px solid #e2e8f0',
                        padding: 16,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 12,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
                    }}>
                        <MwSelector
                            label={t('support.tickets.product')}
                            initalValue={t('support.tickets.allProducts')}
                            dataType='labelValue'
                            _data={productOptions}
                            selectedItem={selectedProductFilter}
                            setSelectedItem={(item) => {
                                const nextValue = item?.value || 'all';
                                const nextParams = new URLSearchParams(searchParams.toString());
                                if (!nextValue || nextValue === 'all') nextParams.delete('product');
                                else nextParams.set('product', String(nextValue));
                                nextParams.delete('page');
                                setSearchParams(nextParams, { replace: true });
                            }}
                            withAddNew={false}
                        />
                        <MwSelector
                            label={t('support.tickets.section')}
                            initalValue={t('support.tickets.allSections')}
                            dataType='labelValue'
                            _data={sectionOptions}
                            selectedItem={selectedSectionFilter}
                            setSelectedItem={(item) => {
                                const nextValue = item?.value || 'all';
                                const nextParams = new URLSearchParams(searchParams.toString());
                                if (!nextValue || nextValue === 'all') nextParams.delete('section');
                                else nextParams.set('section', String(nextValue));
                                nextParams.delete('page');
                                setSearchParams(nextParams, { replace: true });
                            }}
                            withAddNew={false}
                        />
                        <MwSelector
                            label={t('support.tickets.priority')}
                            initalValue={currentLangId === 'ar' ? 'كل الأولويات' : 'All priorities'}
                            dataType='labelValue'
                            _data={priorityOptions}
                            selectedItem={selectedPriorityFilter}
                            setSelectedItem={(item) => {
                                const nextValue = item?.value || 'all';
                                const nextParams = new URLSearchParams(searchParams.toString());
                                if (!nextValue || nextValue === 'all') nextParams.delete('priority');
                                else nextParams.set('priority', String(nextValue));
                                nextParams.delete('page');
                                setSearchParams(nextParams, { replace: true });
                            }}
                            withAddNew={false}
                        />
                    </div>

                    <div style={{
                        background: '#fff',
                        borderRadius: 16,
                        border: '1px solid #e2e8f0',
                        padding: '10px 12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        alignItems: 'center'
                    }}>
                        {[
                            { label: t('support.tickets.allStatuses'), value: 'all', bg: '#f1f5f9', color: '#334155', dot: '#64748b' },
                            ...getLocalizedStatusOptions(supportSettings?.statuses, currentLangId).map((opt) => ({
                                ...opt,
                                ...getStatusAppearance(supportSettings?.statuses, opt.value)
                            }))
                        ].map((opt) => {
                            const isActive = (statusFilterValue || 'all') === opt.value;
                            const count = opt.value === 'all' ? (statusCountRows?.length || 0) : (statusCounts.get(opt.value) || 0);
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                        const nextValue = opt.value || 'all';
                                        setStatusFilterValue(nextValue);
                                        setStatusGroupFilterValue('all');
                                        const nextParams = new URLSearchParams(searchParams.toString());
                                        if (!nextValue || nextValue === 'all') {
                                            nextParams.delete('status');
                                        } else {
                                            nextParams.set('status', String(nextValue));
                                        }
                                        nextParams.delete('statusGroup');
                                        nextParams.delete('page');
                                        setSearchParams(nextParams, { replace: true });
                                    }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '7px 12px',
                                        borderRadius: 999,
                                        border: isActive ? '1px solid #4f46e5' : '1px solid #e2e8f0',
                                        background: isActive ? '#eef2ff' : '#fff',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <span style={{ width: 8, height: 8, borderRadius: 999, background: opt.dot || opt.color || '#64748b' }} />
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{opt.label}</span>
                                    <span style={{
                                        minWidth: 26,
                                        height: 18,
                                        padding: '0 8px',
                                        borderRadius: 999,
                                        background: opt.bg || '#f1f5f9',
                                        color: opt.color || '#334155',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 11,
                                        fontWeight: 900
                                    }}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div style={{
                        background: '#fff',
                        borderRadius: 16,
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 420
                    }}>

                        {/* Header row */}
                        <div style={{
                            display: 'grid', gridTemplateColumns: gridTemplate,
                            padding: '11px 16px',
                            background: '#f8fafc',
                            borderBottom: '2px solid #e2e8f0',
                            gap: 8
                        }}>
                            {cols.map((col, i) => (
                                <div key={i} style={{
                                    fontSize: 10, fontWeight: 800, color: '#94a3b8',
                                    textTransform: 'uppercase', letterSpacing: 1,
                                    textAlign: col.align
                                }}>
                                    {col.key}
                                </div>
                            ))}
                        </div>

                        {/* Data rows */}
                        <div style={{ flex: 1 }}>
                            {loading ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '40px 24px',
                                    color: '#64748b'
                                }}>
                                    <MwSpinnerButton withLabel={false} isFullCenter />
                                </div>
                            ) : filteredRows.length > 0 ? paginatedRows.map((item) => (
                                <Link
                                    key={item._id}
                                    to={`/${params.tenant}/tickets/show/${item._id}`}
                                    style={{ textDecoration: 'none', display: 'block' }}
                                >
                                    <TicketsListItem
                                        lang={currentLangId}
                                        item={item}
                                        gridTemplate={gridTemplate}
                                        supportSettings={supportSettings}
                                    />
                                </Link>
                            )) : (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '40px 24px',
                                    color: '#64748b'
                                }}>
                                    <NoDataFound msg={t('support.tickets.noFilteredTickets')} />
                                </div>
                            )}
                        </div>

                        {filteredRows.length > 0 && hasPaginationControls && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 8,
                                marginTop: 'auto',
                                padding: '16px',
                                borderTop: '1px solid #e2e8f0',
                                background: '#f8fafc'
                            }}>
                                {numberedPagesTotal > 5 && currentPage > 3 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = Math.max(1, currentPage - 1);
                                            const nextParams = new URLSearchParams(searchParams.toString());
                                            if (next <= 1) nextParams.delete('page');
                                            else nextParams.set('page', String(next));
                                            setSearchParams(nextParams, { replace: true });
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: 8,
                                            background: '#e2e8f0',
                                            color: '#334155',
                                            border: 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {isRtl ? <MdKeyboardArrowRight size={20} /> : <MdKeyboardArrowLeft size={20} />}
                                    </button>
                                )}
                                {Array.from({ length: numberedPagesTotal }, (_, i) => i + 1)
                                    .filter((number) => {
                                        if (numberedPagesTotal <= 5) return true;
                                        if (number === 1 || number === numberedPagesTotal) return true;
                                        return Math.abs(currentPage - number) < 3;
                                    })
                                    .map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            onClick={() => {
                                                const nextParams = new URLSearchParams(searchParams.toString());
                                                if (number <= 1) nextParams.delete('page');
                                                else nextParams.set('page', String(number));
                                                setSearchParams(nextParams, { replace: true });
                                            }}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: 8,
                                                background: currentPage === number ? '#4f46e5' : '#e2e8f0',
                                                color: currentPage === number ? '#fff' : '#334155',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                fontWeight: 700
                                            }}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                {numberedPagesTotal > 5 && currentPage < numberedPagesTotal - 2 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = Math.min(numberedPagesTotal, currentPage + 1);
                                            const nextParams = new URLSearchParams(searchParams.toString());
                                            if (next <= 1) nextParams.delete('page');
                                            else nextParams.set('page', String(next));
                                            setSearchParams(nextParams, { replace: true });
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: 8,
                                            background: '#e2e8f0',
                                            color: '#334155',
                                            border: 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {isRtl ? <MdKeyboardArrowLeft size={20} /> : <MdKeyboardArrowRight size={20} />}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '80px 24px', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0'
                }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#cbd5e1" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-3-3v6M3 12a9 9 0 1118 0A9 9 0 013 12z" />
                        </svg>
                    </div>
                    <NoDataFound msg={t('support.tickets.noTickets')} />
                </div>
            )}
        </div>
    );
};

export default TicketsList;
